import React, {
    ChangeEventHandler,
    HTMLInputTypeAttribute,
    MouseEventHandler,
    Ref,
} from 'react';
import styled from 'styled-components';
import Search from 'Assets/Icons/magnifier_search.svg';
import CloseIcon from 'Assets/Icons/close.svg';

type InputPropsT = {
    name: string;
    label: string;
    value: string;
    placeholder?: string;
    errors?: boolean;
    errorLabel?: string | React.ReactElement;
    width?: string;
    mWidth?: string;
    height?: string;
    mHeight?: string;
    margin?: string;
    mMargin?: string;
    type?: string;
    centerErrorLabel?: boolean;
    customIcon?: string;
    iconType?: string;
    iconClick?: Function;
    hideIcon?: boolean;
    hideLabel?: boolean;
    disabled?: boolean;
    themeColor?: string;
    className?: string;
    handleInputs: Function;
    onClickProps?: Function;
    onKeyUpProps?: Function;
    onBlurProps?: Function;
    onFocusProps?: Function;
    autocomplete?: string;
    showCloseIcon?: boolean;
    onCloseAction?: Function;
    dataTestId?: string;
    forwardRef?: Ref<HTMLElement>;
    padding?: string;
    color?: string;
};

const Wrapper = styled.div<Pick<InputPropsT, 'margin' | 'width'>>`
    width: ${(props: any) => props.width || '100%'};
    margin: ${(props: any) => props.margin || '0'};

    @media (max-width: 700px) {
        max-width: ${(props: any) => props.mWidth || '100%'};
        ${(props: any) => (props.mMargin ? `margin: ${props.mMargin}` : '0')};
    }
`;

const InputBox = styled.div<
    Pick<
        InputPropsT,
        | 'hideIcon'
        | 'height'
        | 'mHeight'
        | 'themeColor'
        | 'showCloseIcon'
        | 'errors'
        | 'padding'
    >
>`
    display: ${(props: any) => (props.hideIcon ? 'block' : 'grid')};
    position: relative;
    grid-template-columns: ${(props: any) =>
        props.showCloseIcon ? '30px 1fr 30px' : '30px 1fr'};
    border: 1px solid
        ${(props: any) =>
            props.errors ? '#D83D37' : props.themeColor || '#e7e7f0'};
    box-sizing: border-box;
    border-radius: 8px;
    height: 48px;
    ${(props: any) => (props.height ? `height: ${props.height}` : null)};
    padding: ${(props: any) => (props.padding ? `${props.padding}` : `0 2px`)};

    @media (max-width: 700px) {
        ${(props: any) => (props.mHeight ? `height: ${props.mHeight}` : null)};
    }
`;

const InputWrapper = styled.div`
    position: relative;
    height: 100%;
`;

const InputComponent = styled.input<{ disabled?: boolean; color?: string }>`
    width: 100%;
    height: 100%;
    outline: 0;
    border: none;
    padding-left: 5px;
    font-family: 'Inter';
    font-size: 16px;
    font-weight: 400;
    box-sizing: border-box;

    ${(props: any) =>
        props.color &&
        `color: ${props.color};
`};

    ${(props: any) =>
        props.disabled &&
        `background: none;
`};

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
    }
`;

const IconWrapper = styled.div<Pick<InputPropsT, 'hideIcon'>>`
    display: flex;
    justify-content: center;
    align-items: center;
    display: ${(props: any) => (props.hideIcon ? 'none' : 'flex')};
`;

const CloseIconWrapper = styled.div`
    position: absolute;
    right: 0px;
    top: 30%;
    padding: 0 4px;
    background: white;
`;

const Icon = styled.img<Pick<InputPropsT, 'iconType'>>`
    height: ${(props: any) => (props.iconType == 'address' ? '25px' : '20px')};
    width: ${(props: any) =>
        props.iconType == 'mobile'
            ? '15px'
            : props.iconType == 'address'
              ? '25px'
              : '20px'};
    cursor: ${(props: any) =>
        props.iconType == 'close' ? 'pointer' : 'default'};
`;

const Label = styled.span<{ filled: boolean; themeColor?: string }>`
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: ${(props: any) => props.themeColor || '#5B5675'};
    position: absolute;
    top: ${(props: any) => (props.filled ? '-10px' : '14px')};
    left: ${(props: any) => (props.filled ? '0px' : '5px')};
    background: #fff;
    padding: 0 3px;
    transition: 0.3s ease all;
    pointer-events: none;

    ${InputComponent}:focus ~ &,
  ${InputComponent}:valid {
        top: -10px;
        left: 0px;
    }
`;

const ErrorLabel = styled.div<Pick<InputPropsT, 'centerErrorLabel' | 'margin'>>`
    font-family: 'Inter';
    font-style: normal;
    font-weight: 400;
    color: #d83d37;
    font-size: 12px;
    line-height: 16px;
    text-align: ${(props: any) => (props.centerErrorLabel ? 'center' : 'left')};
    margin: ${(props: any) => (props.margin ? `${props.margin}` : `5px`)};
`;

export function Input(props: InputPropsT) {
    const {
        name,
        label,
        value,
        placeholder,
        errors,
        errorLabel,
        width,
        height,
        mHeight,
        margin,
        type,
        centerErrorLabel,
        iconType,
        iconClick,
        hideLabel,
        className,
        handleInputs,
        themeColor,
        showCloseIcon,
        onCloseAction,
        dataTestId,
        padding,
    } = props;

    return (
        <Wrapper width={width} margin={margin}>
            <InputBox
                hideIcon={!iconType}
                height={height}
                mHeight={mHeight}
                themeColor={themeColor}
                errors={errors}
                showCloseIcon={showCloseIcon}
                padding={padding}
            >
                {iconType && (
                    <IconWrapper
                        data-testid={iconType}
                        hideIcon={!iconType}
                        onClick={() => iconClick && iconClick()}
                    >
                        <Search />
                    </IconWrapper>
                )}
                <InputWrapper>
                    <InputComponent
                        className={className}
                        name={name}
                        value={value}
                        placeholder={placeholder}
                        onChange={handleInputs as ChangeEventHandler}
                        type={type}
                        autoComplete={'none'}
                        data-testid={dataTestId}
                        inputMode={type === 'number' ? 'numeric' : 'text'}
                    />
                    {hideLabel ? null : (
                        <Label
                            themeColor={themeColor}
                            id="inputLabel"
                            filled={!!value}
                        >
                            {label}
                        </Label>
                    )}
                </InputWrapper>
                {showCloseIcon && value?.length > 0 && (
                    <CloseIconWrapper
                        onClick={onCloseAction as MouseEventHandler}
                        data-testid="inputv2-close"
                    >
                        <CloseIcon />
                    </CloseIconWrapper>
                )}
            </InputBox>
            {errors ? (
                <ErrorLabel centerErrorLabel={centerErrorLabel} margin={margin}>
                    {errorLabel}
                </ErrorLabel>
            ) : null}
        </Wrapper>
    );
}
